import type { Dispatch } from 'redux';
import type { TFunction } from 'i18next';

import type ModuleButtonsType from '../components/ModuleButtonsType';
import { showConfirmCancelDialog, showDeleteDialog, showDetailsReportingDialog, Utils } from 'app/main/components';
import { showDocumentSettingsEditDialog } from 'app/main/components/dialogs/DocumentSettingsEditDialog';
import { showAddNotesDialog } from 'app/main/submodules/notes/NotesAddDialog';
import { shareDetailsPage } from 'app/main/components/dialogs/ShareDialog';
import { showFastAddDialog } from '../components/FastAddDialog';

function getCommunicationUsers(params: { dispatch: Dispatch; communicationUid: string }) {
	return Utils.executeStored({
		action: 'st_communications_users_list',
		dispatch: params.dispatch,
		params: {
			n_page: 1,
			n_rows: 1000,
			filters: [
				{ filt_field: 'coms_uid', filt_clause: 'equals', filt_value: params.communicationUid },
				{ filt_field: 'user_enabled', filt_clause: 'equals', filt_value: 1 }
			]
		}
	});
}

function sendCommunicationReminder(params: { dispatch: Dispatch; communication: any; t: TFunction }) {
	const mailMessage = {
		subject: `${import.meta.env.VITE_TITLE} - Reminder Comunicazione`,
		templateName: 'hrm-communication-reminder',
		model: {
			base_url: window.location.origin,
			coms_uid: params.communication.coms_uid,
			coms_title: params.communication.coms_title,
			coms_description: params.communication.coms_description
		}
	};

	getCommunicationUsers({ dispatch: params.dispatch, communicationUid: params.communication.coms_uid }).then(
		response => {
			params.dispatch(
				showConfirmCancelDialog({
					title: params.t('COMMUNICATIONS_REMINDER_DIALOG_TITLE_CONFIRM'),
					onConfirm: () => {
						const bcc = response.data.list?.map(user => user.user_mail) || [];
						Utils.sendMailNotification({ data: { ...mailMessage, bcc } });
					}
				})
			);
		}
	);
}

export default {
	view: {
		itemButtons: [
			// {
			// 	name: 'VIEW_ITEM_BUTTON_FAVOURITE',
			// 	icon: 'star-outline',
			// 	disabled: () => true,
			// 	action: () => {}
			// },
			{
				name: 'VIEW_ITEM_BUTTON_EDIT',
				icon: 'edit',
				hidden: ({ authorizer }) => authorizer.cannot('update', 'communications'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(showFastAddDialog({ module: 'communications', updateRefresh, isUpdate: true, model }))
			},
			{
				name: 'VIEW_ITEM_BUTTON_ADD_COPY',
				icon: 'content-copy',
				iconAdd: true,
				hidden: ({ authorizer }) => authorizer.cannot('create', 'communications'),
				action: ({ model, t }) => {
					const { coms_title } = model;
					Utils.openLink({
						pathname: '/communications/add',
						state: {
							model: { ...model, coms_title: coms_title + t('COPY') }
						}
					});
				}
			},
			{
				name: 'VIEW_ITEM_BUTTON_NEW_NOTE',
				icon: 'comment-text-multiple-outline',
				iconAdd: true,
				action: ({ model, dispatch }) =>
					dispatch(
						showAddNotesDialog({ model: { note_table: 'communications', note_table_uid: model.coms_uid } })
					)
			},
			{
				name: 'VIEW_ITEM_BUTTON_DELETE',
				icon: 'delete',
				color: 'red',
				hidden: ({ authorizer }) => authorizer.cannot('delete', 'communications'),
				action: ({ model, dispatch, updateRefresh }) =>
					dispatch(
						showDeleteDialog({
							module: 'communications',
							id: model.coms_uid,
							title: model.coms_title,
							updateRefresh
						})
					)
			}
		],
		headerButtons: [
			// {
			// 	name: 'VIEW_HEADER_BUTTON_SELECT',
			// 	icon: 'square-outline',
			// 	action: ({ switchIsSelecting }) => switchIsSelecting()
			// },
			{
				name: 'VIEW_HEADER_BUTTON_NEW',
				icon: 'plus-circle',
				hidden: ({ authorizer }) => authorizer.cannot('create', 'communications'),
				action: () => Utils.openLink({ pathname: '/communications/add' })
			}
		]
	},
	details: {
		headerButtons: [
			// {
			// 	name: 'DETAILS_HEADER_BUTTON_FAVOURITE',
			// 	icon: 'star-outline',
			// 	disabled: () => true,
			// 	action: () => {}
			// },
			{
				name: 'DETAILS_COMMUNICATIONS_HEADER_BUTTON_SEND_REMINDER',
				icon: 'email-alert',
				action: ({ dispatch, model, t }) => sendCommunicationReminder({ dispatch, communication: model, t })
			},
			{
				name: 'DETAILS_HEADER_BUTTON_COMMUNICATIONS_DOCUMENTS_SETTINGS',
				icon: 'folder-wrench',
				hidden: ({ authorizer }) => authorizer.cannot('update', 'communications'),
				action: ({ model, dispatch, sysSettings, updateRefresh }) =>
					dispatch(
						showDocumentSettingsEditDialog({
							module: 'communications',
							itemUid: model.coms_uid,
							folderName: model.coms_foldername,
							docsFolderPath: sysSettings.docs_path,
							updateRefresh
						})
					)
			},
			{
				name: 'DETAILS_HEADER_BUTTON_EXPORT',
				icon: 'file-export',
				action: ({ model, dispatch, settings, sysSettings }) =>
					showDetailsReportingDialog({ model, dispatch, settings, sysSettings })
			},
			{
				name: 'DETAILS_HEADER_BUTTON_SHARE',
				icon: 'share-variant',
				action: ({ model, dispatch, settings, t }) => shareDetailsPage({ model, dispatch, settings, t })
			},
			{
				name: 'DETAILS_HEADER_BUTTON_SHOW_LOG',
				icon: 'timeline-clock-outline',
				disabled: ({ isEdit }) => isEdit,
				hidden: ({ authorizer }) => authorizer.cannot('view', 'logs'),
				action: ({ switchShowLogs }) => switchShowLogs()
			},
			{
				name: 'DETAILS_HEADER_BUTTON_DELETE',
				icon: 'delete',
				color: 'red',
				hidden: ({ authorizer }) => authorizer.cannot('delete', 'communications'),
				action: ({ model, dispatch }) =>
					dispatch(
						showDeleteDialog({
							module: 'communications',
							id: model.coms_uid,
							title: model.coms_title
						})
					)
			}
		],
		fabs: [
			{
				id: 'summary',
				name: 'ACTION_EDIT',
				icon: 'edit',
				hidden: ({ authorizer }) => authorizer.cannot('update', 'communications'),
				action: ({ switchEdit }) => switchEdit()
			}
		]
	}
} as ModuleButtonsType;
